/* Storefront guidance only. Scheduled native Shopify discounts remain checkout authority. */
(() => {
  const config = window.BOGO40 || {};
  const eligibleIds = new Set((config.eligibleProductIds || []).map(id => String(id).trim()).filter(Boolean));
  const money = cents => window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(cents, window.theme?.moneyFormat) : `₦${(cents / 100).toLocaleString()}`;

  function groupedEligibleStyles(items) {
    const styles = new Map();
    items.forEach(item => {
      if (!eligibleIds.has(String(item.product_id))) return;
      const style = styles.get(item.product_id) || { title: item.product_title, quantity: 0, lowest: Infinity, variantId: item.variant_id, url: item.url };
      style.quantity += item.quantity;
      style.lowest = Math.min(style.lowest, item.original_price || item.price);
      styles.set(item.product_id, style);
    });
    return [...styles.values()];
  }

  class Bogo40CartStatus extends HTMLElement {
    connectedCallback() {
      this.onCartUpdated = () => this.refresh();
      document.addEventListener('bogo40:cart-updated', this.onCartUpdated);
      this.refresh();
    }

    disconnectedCallback() { document.removeEventListener('bogo40:cart-updated', this.onCartUpdated); }

    async refresh() {
      if (!eligibleIds.size) return;
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error();
        const cart = await response.json();
        const allStyles = groupedEligibleStyles(cart.items);
        const eligibleQuantity = allStyles.reduce((total, style) => total + style.quantity, 0);
        const hasNativeBogoDiscount = cart.items.some(item => (item.line_level_discount_allocations || []).some(allocation => /bogo/i.test(allocation.discount_application?.title || allocation.title || ''))) || (cart.cart_level_discount_applications || []).some(application => /bogo/i.test(application.title || ''));
        this.render(allStyles, eligibleQuantity, hasNativeBogoDiscount);
      } catch (_) { /* Cart guidance must never block shopping. */ }
    }

    render(allStyles, eligibleQuantity, hasNativeBogoDiscount) {
      if (eligibleQuantity >= 2) {
        const names = allStyles.map(style => style.title).join(' and ');
        if (hasNativeBogoDiscount) {
          this.innerHTML = `<div class="bogo40-cart-status__qualified"><strong>${this.copy('appliedHeading', 'BOGO40 applied')}</strong><p>${this.copy('appliedText', 'Your 40% BOGO40 discount has been applied and will carry through to checkout.', { product: names })}</p></div>`;
          return;
        }
        this.innerHTML = `<div class="bogo40-cart-status__qualified"><strong>${this.copy('unlockedHeading', 'BOGO40 unlocked')}</strong><p>${this.copy('unlockedText', 'Your 40% BOGO40 discount will apply automatically at checkout.', { product: names })}</p></div>`;
        return;
      }
      if (!allStyles.length) {
        this.innerHTML = '<div class="bogo40-cart-status__prompt"><strong>Buy 1 wig, get a 2nd 40% OFF</strong><p>Choose any two eligible wigs to unlock 40% off one.</p></div>';
        return;
      }
      const first = allStyles[0];
      const collectionUrl = config.eligibleCollectionUrl || first.url;
      this.innerHTML = `<div class="bogo40-cart-status__prompt"><strong>${this.copy('singleHeading', 'Get 40% off a second eligible wig', { product: first.title })}</strong><p>${this.copy('singleText', 'Choose any other eligible style. The discount applies automatically at checkout.', { product: first.title })}</p><a class="bogo40-cart-status__action" href="${this.escape(collectionUrl)}">${this.copy('singleButton', 'Choose your style')}</a></div>`;
    }

    escape(value) { const node = document.createElement('span'); node.textContent = value; return node.innerHTML; }

    copy(key, fallback, replacements = {}) {
      let value = String((config.cartStatus || {})[key] || fallback);
      Object.entries(replacements).forEach(([token, replacement]) => { value = value.split(`{${token}}`).join(replacement); });
      return this.escape(value);
    }

    bindActions() { this.querySelectorAll('[data-bogo40-add-variant]').forEach(button => button.addEventListener('click', () => this.addSecond(button))); }

    async addSecond(button) {
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        const response = await fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: button.dataset.bogo40AddVariant, quantity: 1 }], sections: ['cart-drawer'], sections_url: window.location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error();
        this.replaceCartDrawer(result.sections?.['cart-drawer']);
      } catch (_) { button.disabled = false; button.textContent = 'Try again'; }
    }

    replaceCartDrawer(html) {
      const current = document.querySelector('.js-cart-drawer-wrapper');
      const replacement = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.js-cart-drawer-wrapper');
      if (current && replacement) current.innerHTML = replacement.innerHTML;
      document.dispatchEvent(new CustomEvent('bogo40:cart-updated'));
    }
  }

  class Bogo40ProductOffer extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.choices = this.querySelectorAll('[data-bogo40-mode]');
      this.second = this.querySelector('.bogo40-product-offer__second');
      this.secondChoices = this.querySelectorAll('[data-bogo40-second-variant]');
      this.submit = this.querySelector('.bogo40-product-offer__submit');
      this.shipping = this.querySelector('.bogo40-product-offer__shipping');
      this.error = this.querySelector('.bogo40-product-offer__error');
      this.variants = JSON.parse(this.querySelector('[data-bogo40-product-variants]')?.textContent || '[]');
      this.mode = 'one';
      this.choices.forEach(choice => choice.addEventListener('click', () => this.setMode(choice.dataset.bogo40Mode)));
      this.secondChoices.forEach(choice => choice.addEventListener('click', () => this.setSecondVariant(choice.dataset.bogo40SecondVariant)));
      this.submit.addEventListener('click', () => this.addSelection());
      this.form?.addEventListener('change', () => { this.syncSecondToFirst(); this.update(); });
      this.syncSecondToFirst();
      this.update();
      if (new URLSearchParams(window.location.search).get('bogo40') === '2') this.setMode('two');
    }

    firstVariant() { return this.variants.find(variant => String(variant.id) === String(this.form?.querySelector('[name="id"]')?.value)); }
    secondVariant() { return this.variants.find(variant => String(variant.id) === String(this.querySelector('.bogo40-product-offer__second-option.is-selected')?.dataset.bogo40SecondVariant)); }

    setMode(mode) {
      this.mode = mode;
      const pairMode = this.mode === 'two';
      this.second.hidden = !pairMode;
      this.shipping.hidden = !pairMode;
      this.choices.forEach(choice => {
        const selected = choice.dataset.bogo40Mode === mode;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-checked', String(selected));
      });
      this.update();
    }

    setSecondVariant(id) {
      this.secondChoices.forEach(choice => {
        const selected = String(choice.dataset.bogo40SecondVariant) === String(id);
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-checked', String(selected));
      });
      this.update();
    }

    syncSecondToFirst() {
      const first = this.firstVariant();
      if (first && this.querySelector(`[data-bogo40-second-variant="${first.id}"]`)) this.setSecondVariant(first.id);
    }

    update() {
      const first = this.firstVariant();
      const second = this.secondVariant();
      if (!first) return;
      const pairTotal = second ? first.price + second.price - Math.round(Math.min(first.price, second.price) * 0.4) : first.price * 1.6;
      const pairChoice = this.querySelector('[data-bogo40-mode="two"]');
      pairChoice.querySelector('[data-bogo40-regular-pair]').textContent = money(first.price + (second?.price || first.price));
      pairChoice.querySelector('[data-bogo40-pair-total]').textContent = money(pairTotal);
      pairChoice.querySelector('i').textContent = `Save ${money(second ? Math.round(Math.min(first.price, second.price) * 0.4) : Math.round(first.price * 0.4))}`;
      this.submit.textContent = this.mode === 'two' ? `Add 2 To Cart · ${money(pairTotal)}` : `Add To Cart · ${money(first.price)}`;
    }

    showError(message) { this.error.textContent = message; this.error.hidden = false; }

    addSelection() {
      this.error.hidden = true;
      if (this.mode === 'one') return this.form?.requestSubmit();
      this.addPair();
    }

    async addPair() {
      const firstVariant = this.form?.querySelector('[name="id"]')?.value;
      const secondVariant = this.secondVariant()?.id;
      if (!firstVariant || !secondVariant) return this.showError('Choose two available options before adding your pair.');
      const button = this.submit;
      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding your pair...';
      try {
        const response = await fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: firstVariant, quantity: 1 }, { id: secondVariant, quantity: 1 }], sections: ['cart-drawer'], sections_url: window.location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Your pair could not be added.');
        const html = result.sections?.['cart-drawer'];
        const current = document.querySelector('.js-cart-drawer-wrapper');
        const replacement = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.js-cart-drawer-wrapper');
        if (current && replacement) current.innerHTML = replacement.innerHTML;
        document.dispatchEvent(new CustomEvent('bogo40:cart-updated'));
        document.querySelector('sht-cart-drwr')?.openDrawer?.();
      } catch (error) { this.showError(error.message || 'Your pair could not be added. Please try again.'); }
      finally { button.disabled = false; button.textContent = originalLabel; }
    }
  }

  if (!customElements.get('bogo40-cart-status')) customElements.define('bogo40-cart-status', Bogo40CartStatus);
  if (!customElements.get('bogo40-product-offer')) customElements.define('bogo40-product-offer', Bogo40ProductOffer);
})();
